import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Save, XCircle } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { authEnabled } from "../auth/config";
import {
  type AiConfig,
  type ModelOption,
  AI_CONFIG_DEFAULTS,
  fetchModels,
  getDocFields,
  putOverride,
  testModel,
  useOverlayVersion,
} from "../lib/overlay";

// Admin-only model settings for the Ask-AI chat (Amazon Bedrock). Lists the account's active
// Anthropic inference profiles, tunes the common parameters, tests access (without enabling
// anything), and saves to the "ai-config" overlay item the chat handler reads at runtime.

const AI_CONFIG_ID = "ai-config";

function currentConfig(): AiConfig {
  try {
    const raw = getDocFields(AI_CONFIG_ID).config;
    if (raw) return { ...AI_CONFIG_DEFAULTS, ...(JSON.parse(raw) as Partial<AiConfig>) };
  } catch {
    /* fall back to defaults */
  }
  return AI_CONFIG_DEFAULTS;
}

export function ModelSettings() {
  // authEnabled is a build-time constant, so branching before any hook is safe.
  if (!authEnabled) return <Notice body="Sign-in isn't configured in this build." />;
  return <Inner />;
}

function Notice({ body }: { body: string }) {
  return (
    <div className="wrap section">
      <p className="eyebrow">Admin · model settings</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        Model settings
      </h1>
      <p className="section-sub">{body}</p>
    </div>
  );
}

type TestState = { state: "idle" | "running" | "ok" | "fail"; msg?: string };

function Inner() {
  const { isAdmin, ready, getApiToken } = useAuth();
  useOverlayVersion();
  const [cfg, setCfg] = useState<AiConfig>(currentConfig);
  const [models, setModels] = useState<ModelOption[] | null>(null);
  const [modelsState, setModelsState] = useState<"loading" | "idle" | "error">("loading");
  const [test, setTest] = useState<TestState>({ state: "idle" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Load the model list once MSAL has restored the session. Gating on `ready` matters: on a
  // direct load/refresh of this page the cached account isn't hydrated at first mount, so an
  // eager getApiToken() returns null (no request made, no sign-in log) and the dropdown would
  // stick on "error" with no retry.
  useEffect(() => {
    if (!ready || !isAdmin) return;
    let live = true;
    void (async () => {
      const token = await getApiToken();
      const list = token ? await fetchModels(token) : null;
      if (!live) return;
      if (list) {
        setModels(list);
        setModelsState("idle");
      } else {
        setModelsState("error");
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isAdmin]);

  if (!isAdmin) return <Notice body="This page is for the site admin. Please sign in." />;

  const set = (patch: Partial<AiConfig>) => setCfg((c) => ({ ...c, ...patch }));

  const runTest = async () => {
    setTest({ state: "running" });
    const token = await getApiToken();
    if (!token) {
      setTest({ state: "fail", msg: "Your sign-in session expired — please sign in again." });
      return;
    }
    const r = await testModel(token, cfg.model.trim());
    if (r.ok) {
      setTest({ state: "ok", msg: "Model responded — access is enabled." });
    } else {
      setTest({
        state: "fail",
        msg: r.notEnabled
          ? "Not enabled in this account. Enable it through your Bedrock governance (Control Tower / Organizations), then test again."
          : r.error ?? "Test failed.",
      });
    }
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    setMsg(null);
    const token = await getApiToken();
    if (!token) {
      setErr("Your sign-in session expired — please sign in again.");
      setSaving(false);
      return;
    }
    const ok = await putOverride(
      AI_CONFIG_ID,
      { stage: "published", fields: { config: JSON.stringify(cfg) } },
      token,
    );
    setSaving(false);
    if (ok) setMsg("Saved — new Ask-AI calls pick these up within a minute.");
    else setErr("Save failed — your session may have expired. Sign out, sign back in, and retry.");
  };

  const knownModel = models?.some((m) => m.id === cfg.model);

  return (
    <div className="wrap section cfg-page">
      <p className="eyebrow">Admin · model settings</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        Model settings
      </h1>
      <p className="section-sub" style={{ maxWidth: "70ch" }}>
        The model and tuning for the Ask-AI chat (Amazon Bedrock). Changes take effect without a
        redeploy. Enabling models and subscriptions is governed in your AWS account, not here.
      </p>

      <div className="cfg-page-body cfg-form">
        <label className="cfg-field">
          <span>Model</span>
          {modelsState === "loading" ? (
            <p className="cfg-soon-note mono">Loading models from Bedrock…</p>
          ) : models && models.length ? (
            <select
              value={cfg.model}
              onChange={(e) => {
                set({ model: e.target.value });
                setTest({ state: "idle" });
              }}
            >
              {!knownModel && <option value={cfg.model}>{cfg.model} (current)</option>}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={cfg.model}
              onChange={(e) => {
                set({ model: e.target.value });
                setTest({ state: "idle" });
              }}
              placeholder="us.anthropic.claude-haiku-4-5-20251001-v1:0"
            />
          )}
          {modelsState === "error" && (
            <span className="cfg-field-err">
              Couldn't list models from Bedrock — type an inference-profile id and use Test.
            </span>
          )}
        </label>

        <div className="cfg-test-row">
          <button
            type="button"
            className="btn btn-sm"
            onClick={runTest}
            disabled={test.state === "running" || !cfg.model.trim()}
          >
            {test.state === "running" ? (
              <Loader2 size={14} className="cs-spin" />
            ) : test.state === "ok" ? (
              <CheckCircle2 size={14} />
            ) : test.state === "fail" ? (
              <XCircle size={14} />
            ) : (
              <CheckCircle2 size={14} style={{ opacity: 0.5 }} />
            )}
            {test.state === "running" ? "Testing…" : "Test model"}
          </button>
          {test.msg && (
            <span className={`cfg-test-msg ${test.state === "ok" ? "is-ok" : test.state === "fail" ? "is-fail" : ""}`}>
              {test.msg}
            </span>
          )}
        </div>

        <div className="cfg-row2">
          <label className="cfg-field">
            <span>
              Max tokens <span className="cfg-opt">answer length cap</span>
            </span>
            <input
              type="number"
              min={1}
              max={2000}
              value={cfg.maxTokens}
              onChange={(e) => set({ maxTokens: Number(e.target.value) })}
            />
          </label>
          <label className="cfg-field">
            <span>
              Temperature <span className="cfg-opt">0 = focused, 1 = creative</span>
            </span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.1}
              value={cfg.temperature}
              onChange={(e) => set({ temperature: Number(e.target.value) })}
            />
          </label>
        </div>

        <div className="cfg-row2">
          <label className="cfg-field">
            <span>
              Top-P <span className="cfg-opt">optional — blank = model default</span>
            </span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={cfg.topP ?? ""}
              onChange={(e) => set({ topP: e.target.value === "" ? null : Number(e.target.value) })}
            />
          </label>
          <label className="cfg-field">
            <span>
              Corpus cap <span className="cfg-opt">chars of résumé sent</span>
            </span>
            <input
              type="number"
              min={1000}
              max={80000}
              step={1000}
              value={cfg.corpusCap}
              onChange={(e) => set({ corpusCap: Number(e.target.value) })}
            />
          </label>
        </div>

        <label className="cfg-field">
          <span>
            System-prompt preamble <span className="cfg-opt">optional — added on top of the fixed rules</span>
          </span>
          <textarea
            rows={4}
            maxLength={2000}
            value={cfg.systemPreamble}
            placeholder="e.g. Prefer a confident, executive tone. Lead with outcomes."
            onChange={(e) => set({ systemPreamble: e.target.value })}
          />
        </label>
        <p className="cfg-soon-note mono">
          The core grounding, anti-hallucination, and anonymized-client rules are always applied; this
          preamble is added on top, not a replacement.
        </p>

        {(msg || err) && <p className={err ? "cfg-err" : "cs-msg"}>{err ?? msg}</p>}
      </div>

      <div className="cfg-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            setCfg(AI_CONFIG_DEFAULTS);
            setTest({ state: "idle" });
          }}
          disabled={saving}
        >
          Reset to defaults
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
